import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@mui/material';

import {
  AppFile,
  DillPaymentRequestResponse,
  DillTransaction,
  formatMoney,
} from '@dill/dill-shared';
import { AppButton } from '../../../../general/AppButton/AppButton';
import { AppPaidInvoicesModal } from '../../../../general/AppPaidInvoicesModal/AppPaidInvoicesModal';
import { Send, X } from 'react-feather';
import { COLORS } from '../../../../../utils/colors';
import { useAppDispatch, useAppSelector } from '../../../../../redux/store';
import { CheckCircle, CircleDashed } from 'phosphor-react';
import { getSupplierPaymentRequest } from '../../../../../redux/services/paymentRequestsService';
import moment from 'moment';
import AppTransactionStatusPill from '../../../../general/AppTransactionStatusPill/AppTransactionStatusPill';
import { Ellipsis } from 'react-css-spinners';
import { getTransactionById } from '../../../../../redux/services/paymentsService';

export const ViewPaymentRequestModal = ({
  open,
  handleClose = () => {},
  selectedPaymentRequest,
}: {
  open: boolean;
  handleClose: () => void;
  selectedPaymentRequest: DillPaymentRequestResponse | null;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const [currentPaymentRequest, setcurrentPaymentRequest] =
    useState<DillPaymentRequestResponse | null>(null);
  const [openPaidInvoicesModal, setOpenPaidInvoicesModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<DillTransaction | null>(null);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);

  const { customers } = useAppSelector((state) => state.supplier);

  const getDetailedPaymentRequest = async (paymentRequestId: string) => {
    setIsLoadingTransactions(true);

    try {
      const resp = await dispatch(
        getSupplierPaymentRequest({
          supplierId: currentSupplier?.id || '',
          paymentRequestId: paymentRequestId,
        })
      );
      if (resp.type === 'paymentRequests/getSupplierPaymentRequest/fulfilled') {
        if (resp.payload) {
          const payload: any = resp.payload;
          if (payload.data.data) {
            setcurrentPaymentRequest(payload.data.data);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching payment request details:', error);
    } finally {
      setIsLoadingTransactions(false);
    }
  };

  const handleClosePaidInvoicesModal = () => {
    setOpenPaidInvoicesModal(false);
    setSelectedTransaction(null);
  };

  useEffect(() => {
    if (selectedPaymentRequest && open) {
      getDetailedPaymentRequest(selectedPaymentRequest.id);
      setcurrentPaymentRequest(selectedPaymentRequest);
    }
    return () => {};
  }, [selectedPaymentRequest, open]);

  const handleCloseModal = () => {
    handleClose();
  };

  const fetchedSupplierBuyers = useMemo(() => {
    if (customers.length > 0) {
      return customers;
    }
    return [];
  }, [customers]);

  const selectedCustomer = useMemo(() => {
    if (currentPaymentRequest?.buyer) {
      return fetchedSupplierBuyers.find(
        (item) => item.buyer.id === currentPaymentRequest?.buyer?.id
      );
    }
    return null;
  }, [currentPaymentRequest, fetchedSupplierBuyers]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getWhoPaysFeeText = (
    whoPaysFee: string,
    buyerTakeRate: number,
    supplierTakeRate: number
  ) => {
    switch (whoPaysFee) {
      case 'SUPPLIER':
        return 'Fees paid by supplier';
      case 'BUYER':
        return 'Fees paid by contractor';
      case 'BOTH':
        return `Fees split: ${buyerTakeRate.toFixed(2)}% contractor, ${(
          supplierTakeRate - buyerTakeRate
        ).toFixed(2)}% supplier`;
      default:
        return 'Not specified';
    }
  };

  return (
    <>
      <AppPaidInvoicesModal
        open={openPaidInvoicesModal}
        handleClose={handleClosePaidInvoicesModal}
        accountType="supplier"
        selectedTransaction={selectedTransaction}
        onViewMainPayment={() => {}}
      />
      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <div className="flex min-h-80 w-5/12 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg flex-col py-1">
          <div className="flex justify-between items-center mb-2 px-3 GREY_200-BORDER border-b py-1">
            <div className="flex">
              <h3 className="font-medium text-lg">Payment Request Details</h3>
            </div>
            <AppButton
              type="FLAT"
              buttonStyles={{ padding: '0px', margin: '5px 2px' }}
              icon={<X color={COLORS.GREY_300} size={30} />}
              onClick={handleCloseModal}
            />
          </div>
          <div className="w-full mb-3 px-3 max-h-[calc(100vh-180px)] overflow-y-auto">
            {currentPaymentRequest ? (
              <div className="flex flex-col space-y-4">
                {/* Customer Information */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Customer Information</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">Customer Name:</span>
                      <p className="text-sm font-medium">
                        {selectedCustomer?.buyer?.name || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Email:</span>
                      <p className="text-sm font-medium">
                        {currentPaymentRequest.emails?.[0] || 'Not specified'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Payment Information</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">Amount:</span>
                      <p className="text-sm font-medium text-green-600">
                        {formatMoney(currentPaymentRequest.totalAmount || 0, 2)}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Fee Structure:</span>
                      <p className="text-sm font-medium">
                        {getWhoPaysFeeText(
                          currentPaymentRequest.whoPaysFee,
                          currentPaymentRequest.buyerTakeRate || 0,
                          currentSupplier?.creditCardTakeRate || 3.5
                        )}
                      </p>
                    </div>
                    {(currentSupplier?.cardPaymentProvider === 'STRIPE' ||
                      !currentSupplier?.cardPaymentProvider ||
                      currentSupplier?.cybersourceSettings?.is3DSEnabled) && (
                      <div>
                        <span className="text-xs text-gray-500">3D Secure:</span>
                        <p className="text-sm font-medium">
                          {currentPaymentRequest.is3DSecure ? 'Enabled' : 'Disabled'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Requested by */}
                {currentPaymentRequest?.senderUser && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Requested By</h4>
                    <p className="text-sm">{currentPaymentRequest?.senderUser?.email}</p>
                  </div>
                )}

                {/* Notes */}
                {currentPaymentRequest.notes && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-semibold text-sm text-gray-700 mb-2">Notes</h4>
                    <p className="text-sm">{currentPaymentRequest.notes}</p>
                  </div>
                )}

                {/* Attachments */}
                {currentPaymentRequest.attachments &&
                  currentPaymentRequest.attachments.length > 0 && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">Attachments</h4>
                      <div className="space-y-1">
                        {currentPaymentRequest.attachments.map((file: AppFile, index: number) => (
                          <div key={index} className="flex items-center space-x-2">
                            <span className="text-xs text-gray-500">📎</span>
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:text-blue-800 underline">
                              {file.name}
                            </a>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Request Details */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Request Details</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">Status:</span>
                      <div className="w-fit">
                        {currentPaymentRequest.status === 'PAID' && (
                          <div className="flex SUCCESS_100-BG text-xs SUCCESS_500-CLR font-semibold px-2 py-1 rounded-full items-center">
                            <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                            <div className="ml-2 text-xs">Paid</div>
                          </div>
                        )}
                        {currentPaymentRequest.status === 'UNPAID' && (
                          <div className="flex PRIMARY_100-BG text-xs PRIMARY_500-CLR font-semibold px-4 py-1 rounded-full items-center">
                            <Send size={14} color={COLORS.PRIMARY_500} />
                            <div className="ml-2 text-xs">Sent</div>
                          </div>
                        )}
                        {currentPaymentRequest.status === 'PAYMENT_INITIATED' && (
                          <div className="flex INFO_100-BG text-xs INFO_500-CLR font-semibold px-1 py-1 rounded-full items-center">
                            <CircleDashed size={20} color={COLORS.INFO_500} />
                            <div className="ml-1 text-2xs">Payment Initiated</div>
                          </div>
                        )}
                        {currentPaymentRequest.status === 'PATIALLY_PAID' && (
                          <div className="flex SUCCESS_100-BG text-xs SUCCESS_500-CLR font-semibold px-1 py-1 rounded-full items-center">
                            <CheckCircle size={14} color={COLORS.SUCCESS_500} />
                            <div className="ml-2 text-2xs">Partially Paid</div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Created:</span>
                      <p className="text-sm font-medium">
                        {currentPaymentRequest.createdAt
                          ? formatDate(currentPaymentRequest.createdAt.toString())
                          : 'Not available'}
                      </p>
                    </div>
                    {currentPaymentRequest.updatedAt && (
                      <div>
                        <span className="text-xs text-gray-500">Last Updated:</span>
                        <p className="text-sm font-medium">
                          {formatDate(currentPaymentRequest.updatedAt.toString())}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Transactions List */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Transactions</h4>
                  <div className="space-y-2">
                    {isLoadingTransactions ? (
                      <div className="flex flex-col items-center justify-center py-4">
                        <Ellipsis color="#3762FB" size={40} />
                        <span className=" text-sm text-gray-500">Loading transactions...</span>
                      </div>
                    ) : currentPaymentRequest.transactions &&
                      currentPaymentRequest.transactions.length > 0 ? (
                      currentPaymentRequest.transactions
                        .sort((a, b) => {
                          const dateA = new Date(a.createdAt || 0);
                          const dateB = new Date(b.createdAt || 0);
                          return dateB.getTime() - dateA.getTime(); // Sort by newest first
                        })
                        .map((transaction, index) => (
                          <div
                            key={transaction.id}
                            className="flex items-center justify-between p-3 bg-white rounded-lg border cursor-pointer hover:bg-gray-50 transition-colors"
                            onClick={() => {
                              (async () => {
                                const transactionResults = await dispatch(
                                  getTransactionById({
                                    transactionId: transaction.id || '',
                                  })
                                );
                                let finalTransaction = null;
                                if (
                                  transactionResults.type ===
                                  'payments/getTransactionById/fulfilled'
                                ) {
                                  const res = transactionResults?.payload as any;
                                  finalTransaction = res.data as DillTransaction;
                                }
                                if (finalTransaction) {
                                  setSelectedTransaction(finalTransaction);
                                  setOpenPaidInvoicesModal(true);
                                }
                              })();
                            }}>
                            <div className="flex-1 flex-col">
                              <div className="flex items-center space-x-3">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900 capitalize">
                                    {transaction.type?.replace(/_/g, ' ').toLowerCase()}
                                  </span>
                                  <div className="text-sm font-semibold text-green-600">
                                    {formatMoney(transaction.totalAmountPaid, 2)}
                                  </div>
                                  <span className="text-xs text-gray-500">
                                    {transaction.createdAt
                                      ? moment(transaction.createdAt).format('MMM DD, YYYY HH:mm')
                                      : 'Date not available'}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center">
                                <AppTransactionStatusPill status={transaction?.transactionStatus} />
                              </div>
                              <div className="text-gray-400">
                                <svg
                                  className="w-4 h-4"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))
                    ) : (
                      <div className="flex items-center justify-center py-4">
                        <span className="text-sm text-gray-500">No transactions found</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-32">
                <p className="text-gray-500">No payment request selected</p>
              </div>
            )}
          </div>
          <div className="mb-2 pt-2 w-full flex justify-end px-3 GREY_200-BORDER border-t">
            <AppButton text="Close" type="TERTIARY" onClick={handleCloseModal} />
          </div>
        </div>
      </Modal>
    </>
  );
};
